import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen bg-void flex flex-col">
      <div className="px-6 md:px-12 h-16 flex items-center justify-between border-b border-ink/5">
        <Link to="/" className="font-serif text-2xl text-ink" data-testid="brand-logo">AI Lernwerk</Link>
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md animate-fade-up">
          <h1 className="font-serif text-4xl text-ink mb-8">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export const GoogleButton = () => {
  const { t } = useI18n();
  return (
    <button onClick={googleLogin} data-testid="google-login-button"
      className="w-full border border-ink/20 text-ink py-3 hover:border-gold hover:text-gold transition-colors">
      {t("auth.google")}
    </button>
  );
};

export const inputCls =
  "w-full bg-surface border border-ink/10 px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

export default function Login() {
  const { t } = useI18n();
  const { setUser, applyUserLang } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      applyUserLang(data.user);
      nav(data.user.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.login.title")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input type="email" required placeholder={t("auth.email")} value={email}
          onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" />
        <input type="password" required placeholder={t("auth.password")} value={password}
          onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password" />
        {error && <p className="text-destructive text-sm" data-testid="login-error">{error}</p>}
        <div className="text-right">
          <Link to="/forgot-password" className="text-xs text-ink-muted hover:text-gold transition-colors" data-testid="forgot-link">
            {t("auth.forgot")}
          </Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full bg-gold text-void py-3 font-medium hover:bg-gold-hover transition-colors disabled:opacity-50">
          {loading ? t("common.loading") : t("auth.login.btn")}
        </button>
      </form>
      <div className="flex items-center gap-4 my-6 text-ink-muted text-xs">
        <div className="flex-1 h-px bg-ink/10" /> {t("auth.or")} <div className="flex-1 h-px bg-ink/10" />
      </div>
      <GoogleButton />
      <p className="text-center text-sm text-ink-muted mt-8">
        {t("auth.noaccount")}{" "}
        <Link to="/register" className="text-gold hover:text-gold-hover" data-testid="to-register">{t("auth.toRegister")}</Link>
      </p>
    </AuthShell>
  );
}
